/**
 * Sentry Configuration
 * Comprehensive error tracking, performance monitoring, and session replay setup
 */

import * as Sentry from '@sentry/react';
import { setTag, browserTracingIntegration, replayIntegration } from './sentry-utils';

// Environment variables
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || '0.0.0';
/**
 * A sample rate from the environment, or the fallback when it is unset or not a number.
 * `Number(v) || 1.0` read an explicit 0 ("off") as 1.0 — every session recorded and traced —
 * which floods Sentry until it rate-limits (429) and the SDK logs each rejection.
 */
export function sampleRate(value: string | undefined, fallback: number): number {
  if (value === undefined || value.trim() === '') return fallback;
  const rate = Number(value);
  return Number.isFinite(rate) && rate >= 0 && rate <= 1 ? rate : fallback;
}

const SENTRY_TRACES_SAMPLE_RATE = sampleRate(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE, 1.0);
const SENTRY_REPLAY_SAMPLE_RATE = sampleRate(import.meta.env.VITE_SENTRY_REPLAY_SAMPLE_RATE, 1.0);
const SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE = 1.0; // Always record sessions with errors
const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED !== 'false';

// Note: Security token is primarily validated server-side via Allowed Domains
// If you need client-side security token support, you may need to use a proxy or
// configure it at the network level (e.g., via reverse proxy)

/**
 * Initialize Sentry with comprehensive monitoring
 */
export function initSentry(): void {
  // Don't initialize if disabled or DSN is missing
  if (!SENTRY_ENABLED || !SENTRY_DSN) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Sentry is disabled or DSN is missing', {
        enabled: SENTRY_ENABLED,
        hasDsn: !!SENTRY_DSN,
        dsnValue: SENTRY_DSN ? '***' : undefined
      });
    }
    return;
  }

  try {
    // Log DSN info for debugging (without exposing the full DSN)
    if (import.meta.env.DEV) {
      const dsnUrl = SENTRY_DSN ? new URL(SENTRY_DSN.replace(/^https?:\/\/([^@]+)@/, 'https://')) : null;
      console.log('[Sentry] Initializing with:', {
        hasDsn: !!SENTRY_DSN,
        sentryHost: dsnUrl?.hostname || 'N/A',
        environment: SENTRY_ENVIRONMENT,
        enabled: SENTRY_ENABLED
      });
    }

    Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment and release tracking
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    
    // PII collection - Disabled for GDPR compliance
    // Set to false to prevent automatic collection of IP addresses, cookies, and other PII
    sendDefaultPii: false,
    
    // Performance monitoring - 100% sample rate for extreme monitoring
    tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
    
    // Session Replay configuration
    replaysSessionSampleRate: SENTRY_REPLAY_SAMPLE_RATE,
    replaysOnErrorSampleRate: SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE,
    
    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      // Note: React Router v7 route tracking is handled automatically by BrowserTracing
      browserTracingIntegration({
        enableInp: true, // Interaction to Next Paint
      }),
      
      // Session Replay
      replayIntegration({
        // Mask all text content and user input by default for privacy
        maskAllText: false,
        blockAllMedia: false,
        // Record console logs
        networkDetailAllowUrls: [
          window.location.origin,
          /^https:\/\/.*\.kim\.cc/,
        ],
      }),
    ],
    
    // Error filtering and enrichment
    beforeSend(event, hint) {
      // Filter out known non-critical errors
      if (event.exception) {
        const error = hint.originalException;
        
        // Ignore network errors that are expected (CORS, network failures)
        if (error instanceof Error) {
          // Ignore ResizeObserver errors (common browser quirk)
          if (error.message?.includes('ResizeObserver loop')) {
            return null;
          }
          
          // Ignore ChunkLoadError (common during deployments)
          if (error.name === 'ChunkLoadError') {
            return null;
          }
        }
      }
      
      // Add custom tags
      event.tags = {
        ...event.tags,
        component: 'frontend',
        framework: 'react',
      };
      
      return event;
    },
    
    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      // Exact: a substring also drops "Failed to fetch dynamically imported module", a page that broke.
      /^Failed to fetch$/,
      // Chrome extensions
      'chrome-extension://',
      'moz-extension://',
    ],
    
    // Additional options
    normalizeDepth: 10, // Increase depth for better error context
    maxBreadcrumbs: 100, // Maximum breadcrumbs to store
    
    // Debug mode in development
    // Opt-in: the dev deploy is a development build, and debug there logged every unsampled
    // span — thousands of console lines a minute.
    debug: import.meta.env.VITE_SENTRY_DEBUG === 'true',
    });
    
    // Set initial tags
    setTag('environment', SENTRY_ENVIRONMENT);
    setTag('release', SENTRY_RELEASE);
    
    if (import.meta.env.DEV) {
      console.log('[Sentry] Initialized successfully', {
        environment: SENTRY_ENVIRONMENT,
        release: SENTRY_RELEASE,
        tracesSampleRate: SENTRY_TRACES_SAMPLE_RATE,
        replaySampleRate: SENTRY_REPLAY_SAMPLE_RATE,
      });
    }
  } catch (error) {
    // Log initialization errors
    console.error('[Sentry] Failed to initialize Sentry:', error);
    if (import.meta.env.DEV) {
      console.error('[Sentry] Initialization error details:', {
        dsn: SENTRY_DSN ? '***' : undefined,
        environment: SENTRY_ENVIRONMENT,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  }
}
